import './style.css';
import { BattleEngine } from './game/engine';
import { FIXED_STEP } from './game/config';
import type { BattleEvent, BattleOptions, BattleSnapshot, GameCommand, Settings } from './game/types';
import { OceanScene } from './graphics/scene';
import { GameView } from './ui/view';
import { loadSettings, saveSettings } from './platform/storage';
import { GameInput } from './platform/input';
import { NavalAudio } from './platform/audio';

let settings: Settings = loadSettings();
let engine: BattleEngine | null = null;
let scene: OceanScene | null = null;
let snapshot: BattleSnapshot | null = null;
let lastOptions: BattleOptions = { ship: 'frigate', map: 'islands', difficulty: 'normal', tutorial: false };
let accumulator = 0;
let uiTimer = 0;
let resultHandled = false;
let tutorialTurned = false;
let tutorialFired = false;
let tutorialSunk = false;
let frameId = 0;
let disposed = false;
const audio = new NavalAudio();
audio.setVolume(settings.volume);

const view = new GameView(document.querySelector<HTMLElement>('#app')!, settings, {
  start: startBattle,
  pause: pauseBattle,
  resume: resumeBattle,
  restart: () => startBattle({ ...lastOptions, seed: undefined }),
  menu: showMenu,
  language: (locale) => {
    input.clear(); engine?.command({ type: 'steer', value: 0 });
    settings = { ...settings, locale }; view.setSettings(settings); view.setLocale(locale); persist();
  },
  quality: (quality) => {
    settings = { ...settings, quality }; scene?.setQuality(quality); view.setSettings(settings); persist();
  },
  volume: (volume) => {
    settings = { ...settings, volume }; audio.setVolume(volume); view.setSettings(settings); persist(); void audio.unlock();
  },
  zoom: (delta) => scene?.zoom(delta),
  previewShip: (kind) => scene?.selectPreview(kind),
  previewRotate: (delta) => scene?.rotatePreview(delta),
  previewZoom: (delta) => scene?.zoomPreview(delta),
  previewReset: () => scene?.resetPreview(),
});
const input = new GameInput(() => {
  if (snapshot?.phase === 'playing') pauseBattle();
  else if (snapshot?.phase === 'paused') resumeBattle();
}, (delta) => scene?.zoom(delta), (side) => engine?.command({ type: 'fire', side }));

function persist(): void {
  if (!saveSettings(settings)) view.showNotice('storageWarning');
}
function startBattle(options: BattleOptions): void {
  if (!scene) { view.showError('bootError'); return; }
  void audio.unlock(); audio.setPlaying(true);
  engine?.dispose(); input.clear();
  lastOptions = { ...options };
  engine = new BattleEngine(lastOptions);
  snapshot = engine.getSnapshot();
  accumulator = 0; uiTimer = 0; resultHandled = false;
  tutorialTurned = false; tutorialFired = false; tutorialSunk = false;
  view.setScreen('playing'); view.setTutorialStep(0); view.render(snapshot);
  // Create this fleet and clear the previous effects before accepting its first shot.
  scene.render(snapshot, 0);
  input.enabled = true;
}
function pauseBattle(): void {
  if (!engine || snapshot?.phase !== 'playing') return;
  engine.command({ type: 'pause' }); snapshot = engine.getSnapshot();
  input.enabled = false; input.clear(); accumulator = 0;
  audio.setPlaying(false); view.setScreen('paused'); view.render(snapshot);
}
function resumeBattle(): void {
  if (!engine || snapshot?.phase !== 'paused' || document.hidden) return;
  engine.command({ type: 'resume' }); snapshot = engine.getSnapshot();
  input.clear(); input.enabled = true; accumulator = 0;
  void audio.unlock(); audio.setPlaying(true); view.setScreen('playing'); view.render(snapshot);
}
function showMenu(): void {
  engine?.dispose(); engine = null; snapshot = null; accumulator = 0;
  input.enabled = false; input.clear(); audio.setPlaying(false);
  view.setSettings(settings); view.setScreen('menu'); view.render(null);
}
function processEvents(events: BattleEvent[]): void {
  scene?.effects(events); audio.events(events);
  if (snapshot?.options.tutorial) {
    if (events.some((event) => event.type === 'fire' && event.faction === 'player')) tutorialFired = true;
    if (events.some((event) => event.type === 'sink' && event.faction === 'enemy')) tutorialSunk = true;
    const step = !tutorialTurned ? 0 : !tutorialFired ? 1 : !tutorialSunk ? 2 : 3;
    view.setTutorialStep(step);
    if (step === 3 && !settings.tutorialDone) {
      settings = { ...settings, tutorialDone: true }; persist(); view.showNotice('tutorialDone');
    }
  }
  const result = events.find((event) => event.type === 'result');
  if (result?.type === 'result' && !resultHandled && snapshot) {
    resultHandled = true; input.enabled = false; input.clear(); accumulator = 0; audio.setPlaying(false);
    const { map, difficulty, ship } = snapshot.options;
    const key = `${map}:${difficulty}:${ship}`;
    if (result.score > (settings.bestScores[key] ?? 0)) {
      settings = { ...settings, bestScores: { ...settings.bestScores, [key]: result.score } }; persist();
    }
    view.setSettings(settings); view.setScreen(result.outcome); view.render(snapshot);
  }
}
function fixedTick(commands?: GameCommand[]): void {
  if (!engine || snapshot?.phase !== 'playing') return;
  const player = snapshot.ships.find((ship) => ship.id === snapshot!.playerId)!;
  for (const command of commands ?? input.poll(player.throttle, FIXED_STEP)) {
    engine.command(command);
    if (command.type === 'steer' && command.value !== 0) tutorialTurned = true;
  }
  engine.step(FIXED_STEP);
  snapshot = engine.getSnapshot();
  processEvents(engine.drainEvents());
}

const canvas = document.querySelector<HTMLCanvasElement>('#ocean')!;
const metrics = new URLSearchParams(location.search).has('metrics') ? document.createElement('output') : null;
if (metrics) {
  metrics.style.cssText = 'position:fixed;bottom:5px;right:8px;z-index:100;pointer-events:none;background:#08242de6;color:#fff;font:11px monospace;padding:4px 7px;border-radius:4px';
  document.body.append(metrics);
}
try {
  scene = new OceanScene(canvas, settings.quality); view.setShipImages(scene.getShipThumbnails());
  // Safari can defer animation frames in an inactive window. Paint the shipyard immediately.
  scene.render(null, 0);
}
catch (error) { console.error('Renderer initialization failed', error); view.showError('bootError'); }

let lastTime = performance.now();
function frame(time: number): void {
  if (disposed) return;
  const delta = Math.min(Math.max((time - lastTime) / 1000, 0), 0.1); lastTime = time;
  if (snapshot?.phase === 'playing' && !document.hidden) {
    accumulator += delta;
    let steps = 0;
    while (accumulator >= FIXED_STEP && steps < 6 && snapshot.phase === 'playing') {
      accumulator -= FIXED_STEP; fixedTick(); steps++;
    }
  } else accumulator = 0;
  scene?.render(snapshot, delta);
  uiTimer += delta;
  if (uiTimer >= 0.05) {
    uiTimer = 0; view.render(snapshot);
    if (metrics && scene) {
      const stats = scene.getStats();
      metrics.textContent = `${stats.fps.toFixed(0)} FPS · ${stats.drawCalls} draws · ${stats.triangles.toLocaleString()} triangles · ${stats.geometries} geometries · ${stats.textures} textures`;
    }
  }
  frameId = requestAnimationFrame(frame);
}
frameId = requestAnimationFrame(frame);
window.addEventListener('resize', () => scene?.resize());
window.addEventListener('blur', () => {
  if (snapshot?.phase === 'playing') { pauseBattle(); view.showNotice('pausedBlur'); }
});
document.addEventListener('visibilitychange', () => {
  if (document.hidden && snapshot?.phase === 'playing') { pauseBattle(); view.showNotice('pausedBlur'); }
  lastTime = performance.now(); accumulator = 0;
});
canvas.addEventListener('webglcontextlost', (event) => {
  event.preventDefault(); pauseBattle(); view.showError('contextLost');
});
window.addEventListener('pagehide', (event) => {
  if (event.persisted) { pauseBattle(); return; }
  disposed = true; cancelAnimationFrame(frameId); engine?.dispose(); scene?.dispose(); input.dispose(); view.dispose(); void audio.dispose();
});
window.addEventListener('pageshow', () => { lastTime = performance.now(); accumulator = 0; });

// Development-only deterministic driver. It uses the same commands and simulation as play,
// and never edits HP, position or result state. Not included in the production build.
if (import.meta.env.DEV && new URLSearchParams(location.search).has('test')) {
  const debug = {
    snapshot: () => snapshot,
    command: (command: GameCommand) => {
      engine?.command(command); snapshot = engine?.getSnapshot() ?? null;
      if (command.type === 'pause') { input.enabled = false; view.setScreen('paused'); }
      if (command.type === 'resume') { input.enabled = true; view.setScreen('playing'); }
    },
    advance: (ticks: number, commands: GameCommand[] = []) => {
      for (let tick = 0; tick < Math.min(ticks, 36000); tick++) fixedTick(commands);
      view.render(snapshot);
      return snapshot;
    },
    stats: () => scene?.getStats(),
    preview: () => scene?.getPreviewState(),
  };
  Object.assign(window, { __bq: debug });
}
